import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

/**
 * Elevation schematic for `dorm-desk`.
 *
 * Front: desktop top band, open kneehole on the left with a thin leg,
 * 3 drawer faces on the right pedestal with arc pulls, and a vertical
 * divider between them.
 * Side: top band only (no base — open underneath for chair clearance).
 *
 * Constants match dormDeskRenderer.js.
 */

const IN = 0.0254;
const TOP_THICK = 1 * IN;
const PANEL_THICK = 0.75 * IN;
const DRAWER_GAP = 0.4 * IN;
const PEDESTAL_W = 14.5 * IN;
const NUM_DRAWERS = 3;
const PULL_W = 5 * IN;
const ARC_RISE = 1 * IN;

export function DormDeskSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <DeskSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT
  const bodyTop = TOP_THICK;
  const bodyBottom = height;
  const bodyH = bodyBottom - bodyTop;
  const drawerH = (bodyH - (NUM_DRAWERS - 1) * DRAWER_GAP) / NUM_DRAWERS;

  // Pedestal sits on the right — divider X from the left edge
  const dividerX = width - PANEL_THICK - PEDESTAL_W;
  const pedestalCenterX = dividerX + PEDESTAL_W / 2;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Top band */}
      <Line
        x1={0}
        y1={bodyTop}
        x2={width}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Left leg inner edge */}
      <Line
        x1={PANEL_THICK}
        y1={bodyTop}
        x2={PANEL_THICK}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Vertical divider between kneehole and pedestal */}
      <Line
        x1={dividerX}
        y1={bodyTop}
        x2={dividerX}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Drawer gap lines (only across the pedestal section) */}
      {Array.from({ length: NUM_DRAWERS - 1 }, (_, g) => {
        const y = bodyTop + (g + 1) * drawerH + g * DRAWER_GAP + DRAWER_GAP / 2;
        return (
          <Line
            key={`gap-${g}`}
            x1={dividerX}
            y1={y}
            x2={width}
            y2={y}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      {/* Arc pulls on each drawer */}
      {Array.from({ length: NUM_DRAWERS }, (_, d) => {
        const cy = bodyTop + d * (drawerH + DRAWER_GAP) + drawerH / 2;
        const x1 = pedestalCenterX - PULL_W / 2;
        const x2 = pedestalCenterX + PULL_W / 2;
        return (
          <path
            key={`pull-${d}`}
            d={`M ${x1} ${cy} Q ${pedestalCenterX} ${cy - ARC_RISE} ${x2} ${cy}`}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </SchematicShell>
  );
}

function DeskSide({ depth, height, strokeColor, strokeWidth }) {
  const bodyTop = TOP_THICK;

  return (
    <g>
      {/* Top band */}
      <Line
        x1={0}
        y1={bodyTop}
        x2={depth}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormDeskSchematic.supportsSideViews = true;
